import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { setAccessToken, setOnLogout } from "../../services/apiV1";
import * as authApi from "./authApi";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const applyAuth = useCallback((data) => {
    setAccessToken(data.accessToken);
    setUser(data.user);
    setPermissions(data.permissions || []);
  }, []);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setPermissions([]);
  }, []);

  // On mount, try to restore the session from the refresh cookie.
  useEffect(() => {
    setOnLogout(clear);
    authApi
      .refresh()
      .then(applyAuth)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applyAuth, clear]);

  const login = useCallback(
    async (creds) => {
      const data = await authApi.login(creds);
      applyAuth(data);
      return data;
    },
    [applyAuth]
  );

  const register = useCallback(
    async (payload) => {
      const data = await authApi.register(payload);
      applyAuth(data);
      return data;
    },
    [applyAuth]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    clear();
  }, [clear]);

  const has = useCallback(
    (perm) => user?.role === "super_admin" || permissions.includes(perm),
    [user, permissions]
  );

  const value = {
    user,
    permissions,
    loading,
    isAuthed: !!user,
    login,
    register,
    logout,
    has,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
